import React, { useEffect, useState } from 'react'
import { Modal, ContextNotification } from '@pandiafi/components'
import { Button, SingleDateInput } from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'
import { DateTime } from 'luxon'

import type { CoreParkingSpotReservation } from '../../../common/validations/contractParkingSpotsSchema'
import { ResidentAppError } from '../../../common/error'
import { todayISO } from './parkingSpots.helpers'

import { useAPITerminateContractParkingSpotReservation } from 'api/apiHooks/useAPITerminateContractParkingSpotReservation'
import { useCurrentContract } from 'hooks/useCurrentContract/useCurrentContract'
import { renderDate, safeISODateFromLocaleDate } from 'utils'

const getMinimumEndDateISO = (reservation: CoreParkingSpotReservation) => {
  const today = todayISO()

  const reservationStartPlusOneDay = DateTime.fromISO(reservation.startDate)
    .plus({ days: 1 })
    .toISODate()

  return reservationStartPlusOneDay && reservationStartPlusOneDay > today
    ? reservationStartPlusOneDay
    : today
}

export const ParkingSpotTerminateForm: React.FC<{
  rentableParkingSpotId: number
  reservation: CoreParkingSpotReservation
  onClose: () => void
}> = ({ rentableParkingSpotId, reservation, onClose }) => {
  const { t } = useTranslation(['parking', 'common'])
  const { currentContractId } = useCurrentContract()

  const minimumEndDateISO = getMinimumEndDateISO(reservation)

  const [endDate, setEndDate] = useState(minimumEndDateISO)
  const [dateError, setDateError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [terminatedISO, setTerminatedISO] = useState<string | null>(null)

  useEffect(() => {
    setEndDate(minimumEndDateISO)
    setDateError(null)
    setError(null)
    setTerminatedISO(null)
  }, [minimumEndDateISO, rentableParkingSpotId])

  const { mutate: terminate, isPending: isTerminating } =
    useAPITerminateContractParkingSpotReservation(currentContractId)

  const submit = () => {
    if (!currentContractId) {
      setError(t('parking:error.contractMissing'))
      return
    }

    // Double check in case user manually edited the date: submit is not disabled for invalid form...
    if (endDate < minimumEndDateISO) {
      setDateError(
        t('parking:terminate.endDateTooEarly', {
          date: renderDate(minimumEndDateISO),
        })
      )
      return
    }

    setDateError(null)
    setError(null)
    terminate(
      { endDate, rentableParkingSpotId: rentableParkingSpotId },
      {
        onSuccess: () => {
          setTerminatedISO(endDate)
        },
        onError: (e) => {
          if (
            e instanceof ResidentAppError &&
            e.errorCode === 'RESERVATION_END_BEFORE_TODAY'
          ) {
            setDateError(
              t('parking:terminate.endDateTooEarly', {
                date: renderDate(minimumEndDateISO),
              })
            )
            return
          }

          setError(t('parking:error.terminationFailed'))
        },
      }
    )
  }

  return (
    <>
      {error && (
        <>
          <Modal.Notifications>
            <ContextNotification type="error">{error}</ContextNotification>
          </Modal.Notifications>
          <Modal.Buttons>
            <Button variant="cancel" onClick={onClose}>
              {t('common:closeButton')}
            </Button>
          </Modal.Buttons>
        </>
      )}

      {terminatedISO ? (
        <>
          <Modal.Notifications>
            <ContextNotification type="success" aria-live="polite">
              {t('parking:terminate.success')} {renderDate(terminatedISO)}
            </ContextNotification>
          </Modal.Notifications>
          <Modal.Buttons>
            <Button variant="cancel" onClick={onClose}>
              {t('common:closeButton')}
            </Button>
          </Modal.Buttons>
        </>
      ) : (
        <>
          <Modal.Content>
            <div className="mb-6">
              <label htmlFor="endDate" className="mb-2 block font-medium">
                {t('parking:terminate.endDateLabel')}*
              </label>
              <SingleDateInput
                id="endDate"
                value={renderDate(endDate)}
                onChange={(val) => {
                  const iso = safeISODateFromLocaleDate(val)
                  if (iso) {
                    setEndDate(iso)
                    if (dateError) setDateError(null)
                  }
                }}
                placeholder={t('common:placeholders.date')}
                datePickerPosition="bottomLeft"
                disabledBefore={DateTime.fromISO(minimumEndDateISO)
                  .startOf('day')
                  .toJSDate()}
              />
              {dateError && (
                <div className="mt-1 text-sm text-common-red-6">
                  {dateError}
                </div>
              )}
            </div>
          </Modal.Content>
          <Modal.Buttons>
            <Button variant="cancel" onClick={onClose}>
              {t('form.actions.cancel')}
            </Button>
            <Button variant="delete" onClick={submit} loading={isTerminating}>
              {t('parking:terminate.cta')}
            </Button>
          </Modal.Buttons>
        </>
      )}
    </>
  )
}
